/**
 * Ouvrir dans l'assistant le fil qu'un dock affiche (#1587).
 *
 * Déclenché par la bascule automatique du dock dès que Hector écrit dans le
 * canvas (#1588) : l'avocat n'a plus de bouton à cliquer pour rejoindre
 * l'écran où le document se rédige.
 *
 * **Fermer le dock AVANT de naviguer, dans le même appel synchrone.** Le dock
 * hors dossier (`DashboardLayout`) n'a aucune frontière de route : laissé
 * ouvert, il restait monté à côté de l'assistant — deux `V3ChatInterface` sur
 * la même session, écouteurs globaux doublés — ou, détaché en `fixed z-40`,
 * par-dessus le canvas. La fermeture réveille le store en priorité synchrone,
 * la navigation de React Router part en transition : l'ordre du code est donc
 * l'ordre des commits. Un `await` entre les deux, ou une fermeture déléguée à
 * un effet de route, recréerait un commit où les deux chats coexistent.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssistantDockStore } from '@/stores/assistantDockStore';
import { releverConversationAffichee } from '../state/conversationAffichee';

interface OuvrirConversationDansLAssistantDeps {
  /** Navigation du routeur ; reste valide après le démontage du dock qui l'a fournie. */
  readonly navigate: (to: string) => void;
  /** Injection de test ; par défaut, `close()` du store du dock. */
  readonly fermerDock?: () => void;
}

/**
 * Ferme le dock puis navigue vers `/assistant?c=<id>`.
 *
 * @throws si `conversationId` est absent ou vide — avant tout effet : un
 *   `/assistant` nu ouvrirait une conversation vierge, et l'avocat croirait
 *   avoir perdu le fil qu'il quittait.
 */
export function ouvrirConversationDansLAssistant(
  conversationId: string | null,
  deps: OuvrirConversationDansLAssistantDeps,
): void {
  if (!conversationId?.trim()) {
    throw new Error(
      'ouvrirConversationDansLAssistant sans conversationId : aucun fil à ouvrir ; '
      + '/assistant nu ouvrirait une conversation vierge. '
      + `Reçu : ${JSON.stringify(conversationId)}. L'hôte doit fournir le fil qu'il affiche.`,
    );
  }
  // Le même identifiant que l'assistant lira dans `?c=` : c'est lui qui
  // reprendra la relève en publiant. Encodé parce qu'il voyage dans l'URL.
  const id = conversationId.trim();
  const destination = `/assistant?c=${encodeURIComponent(id)}`;

  // Le dock démonté par la fermeture n'est plus propriétaire du registre : sans
  // relève, il le reposerait à `null` avant que l'assistant ne le reprenne.
  releverConversationAffichee(id);
  (deps.fermerDock ?? useAssistantDockStore.getState().close)();
  deps.navigate(destination);
}

/** Version React : lie le geste au routeur courant. */
export function useOuvrirConversationDansLAssistant(): (conversationId: string | null) => void {
  const navigate = useNavigate();
  return useCallback(
    (conversationId: string | null) => ouvrirConversationDansLAssistant(conversationId, { navigate }),
    [navigate],
  );
}
